import Phaser from 'phaser';
import { eventBus } from '@/shared/EventBus';
import { HatchDirector, type HatchPayload, SKIP_AVAILABLE_AFTER_MS } from '@/game/fx/HatchDirector';
import { HatchBulkDirector, type HatchBulkPayload } from '@/game/fx/HatchBulkDirector';
import { assetBundleManager } from '@/game/assets/AssetBundleManager';

/** Wspólny interfejs directorów (single + bulk) — minimalna powierzchnia używana przez scenę. */
interface IHatchDirector {
  play(): Promise<void>;
  skip(): void;
  destroy(): void;
}

type HatchScenePayload = (HatchPayload | HatchBulkPayload) & { hatchNonce: number };

/** Discriminator payloadu single vs bulk — bulk ma `pets[]`. */
function isBulkPayload(
  p: HatchScenePayload,
): p is HatchBulkPayload & { hatchNonce: number } {
  return Array.isArray((p as HatchBulkPayload).pets);
}

let hatchRunSeq = 0;

/**
 * HatchScene — runtime owner cinematic'a hatcha (sesja 29 — Step A).
 *
 * Scena overlay launchowana przez `WorldScene` przy odbiorze `hatch:start`.
 * Maluje cinematic ponad WorldScene (snow particles itd. nadal renderują się
 * pod spodem — przyjemny vibe gdy gracz hatchuje w mountain biomie).
 *
 * **Lifecycle:**
 *  1. `init(payload)` — Phaser przekazuje payload przez `scene.launch('HatchScene', payload)`.
 *  2. `create()` — instancja HatchDirector + listener na pointer/skip event.
 *  3. `director.play()` resolveje po końcu animacji LUB po skipie.
 *  4. `complete()` — emit `hatch:complete`, `this.scene.stop()` zatrzymuje scenę.
 *
 * **Skip:**
 *  - Pointer down (tap/click) NA SCENIE HATCHA → spróbuj skip.
 *  - Albo eventBus `hatch:skip` z UI (React może mieć przycisk Skip w przyszłości).
 *  - HatchDirector waliduje czy minęło `SKIP_AVAILABLE_AFTER_MS` (zapobiega
 *    nadrukowanym pierwszym frame'om).
 *
 * **Cleanup:**
 *  - SHUTDOWN handler odpina listenery + woła `director.destroy()` defensywnie.
 *  - Idempotentne — multiple complete() calls są no-op.
 *
 * **Scena overlay vs replacing:**
 *  - Używamy `scene.launch` w WorldScene (nie `scene.start`) — HatchScene
 *    biegnie równolegle, WorldScene update() nie zatrzymuje się. To pozwala
 *    ambient FX (snow) i muzyce/SFX (gdy będą) płynąć podczas hatcha.
 */
export class HatchScene extends Phaser.Scene {
  private director: IHatchDirector | null = null;
  private payload: HatchScenePayload | null = null;
  private completed = false;
  private terminalEventSent = false;
  private runId = '0';
  private hatchNonce = 0;
  private offSkipEvent?: () => void;
  private loadingObjects: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: 'HatchScene' });
  }

  init(payload?: HatchScenePayload): void {
    this.payload = payload ?? null;
    this.completed = false;
    this.terminalEventSent = false;
    this.runId = String(++hatchRunSeq);
    this.hatchNonce = payload?.hatchNonce ?? 0;
    this.loadingObjects = [];
  }

  create(): void {
    if (!this.payload) {
      console.error('[HatchScene] Brak payloadu w init() — przerywam.');
      this.scene.stop();
      return;
    }

    // Scena startuje SYNCHRONICZNIE z eventem Reacta, więc zawsze istnieje
    // właściciel terminalnego hatch:complete. Same grafiki dociąga już ta scena.
    this.showLoadingCover();

    // Pointer input — tap/click po SKIP_AVAILABLE_AFTER_MS triggeruje skip.
    // HatchDirector sam waliduje threshold (defensive — pointer może być
    // emitowany od razu po launchu).
    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.handleSkipInput, this);

    // EventBus skip — przyszły UI button "Pomiń" może emitować ten event.
    this.offSkipEvent = eventBus.on('hatch:skip', () => this.handleSkipInput());

    // SHUTDOWN cleanup — defensywne. Director cleanup się też wywołuje przez
    // complete(), ale to bezpiecznik gdyby scena była zatrzymana zewnętrznie
    // (np. game shutdown podczas hatcha).
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.handleShutdown, this);
    void this.startDirectorWhenReady(this.payload, this.runId);
  }

  private async startDirectorWhenReady(
    payload: HatchScenePayload,
    runId: string,
  ): Promise<void> {
    try {
      await assetBundleManager.ensureHatchAssets(this, payload, runId);
    } catch (err) {
      console.warn('[HatchScene] Ładowanie assetów hatchu nieudane:', err);
      if (runId === this.runId && this.scene.isActive()) this.complete(runId);
      return;
    }
    // Ta sama instancja Scene jest reużywana. Po stop+launch może być ponownie
    // aktywna z innym przebiegiem, więc sam isActive()/completed nie wystarcza.
    if (runId !== this.runId || !this.scene.isActive() || this.completed) return;
    this.destroyLoadingCover();
    this.director = isBulkPayload(payload)
      ? new HatchBulkDirector(this, payload)
      : new HatchDirector(this, payload);
    void this.director.play().then(
      () => this.complete(runId),
      (err) => {
        console.warn('[HatchScene] Director hatchu przerwany błędem:', err);
        this.complete(runId);
      },
    );
  }

  private showLoadingCover(): void {
    const { width, height } = this.scale;
    const backdrop = this.add.rectangle(width / 2, height / 2, width, height, 0x10182a, 0.9)
      .setScrollFactor(0)
      .setDepth(100_000);
    const pulse = this.add.circle(width / 2, height / 2, 18, 0xffffff, 0.88)
      .setScrollFactor(0)
      .setDepth(100_001);
    this.tweens.add({
      targets: pulse,
      scale: 1.8,
      alpha: 0.25,
      duration: 520,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.loadingObjects.push(backdrop, pulse);
  }

  private destroyLoadingCover(): void {
    for (const object of this.loadingObjects) object.destroy();
    this.loadingObjects.length = 0;
  }

  private handleSkipInput(): void {
    if (this.completed) return;
    // HatchDirector.skip() sam waliduje SKIP_AVAILABLE_AFTER_MS. Jeśli za
    // wcześnie, no-op. Jeśli OK, robi complete() → resolveje play() →
    // nasz then() wywołuje this.complete().
    this.director?.skip();
    void SKIP_AVAILABLE_AFTER_MS;   // import-referenca dla bundlera
  }

  /**
   * Zakończ cinematic. Idempotent. Wywoływane przez `director.play().then(...)`
   * (natural end LUB skip). Emituje `hatch:complete` i zatrzymuje scenę.
   */
  private complete(runId: string = this.runId): void {
    if (runId !== this.runId || this.completed) return;
    this.completed = true;

    this.emitTerminalEvent();
    this.cleanup();
    this.scene.stop();
  }

  private handleShutdown(): void {
    this.cleanup();
    this.emitTerminalEvent();
  }

  private emitTerminalEvent(): void {
    if (this.terminalEventSent) return;
    this.terminalEventSent = true;
    eventBus.emit('hatch:complete', { hatchNonce: this.hatchNonce });
  }

  /**
   * Cleanup — odpina listenery, niszczy director. Idempotent.
   * Wołane przez:
   *  - `complete()` (normal flow)
   *  - SHUTDOWN handler (defensive — gdyby scena padła zewnętrznie)
   */
  private cleanup(): void {
    this.input.off(Phaser.Input.Events.POINTER_DOWN, this.handleSkipInput, this);
    this.offSkipEvent?.();
    this.offSkipEvent = undefined;
    this.destroyLoadingCover();
    this.director?.destroy();
    this.director = null;
    assetBundleManager.releaseHatchAssets(this.textures, this.anims, this.runId);
  }
}
